import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, ExternalLink, FileWarning, LogOut, Megaphone, PenLine, CheckCircle2, ClipboardCheck, KeyRound, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import SignaturePad from "@/components/SignaturePad";
import LetterView from "@/components/LetterView";
import PdfViewer from "@/components/PdfViewer";
import StaffEvaluation from "@/components/StaffEvaluation";
import { enablePush, isIOS, isStandalone, pushSupported, registerServiceWorker } from "@/lib/pwa";
import { formatApiError } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IDLE_LIMIT = 30 * 60 * 1000;

const STATUS_TONE = {
  "جديدة": "bg-amber-100 text-amber-800 border-amber-300",
  "تم الإرسال": "bg-blue-100 text-blue-800 border-blue-300",
  "تم الرد": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "مغلقة": "bg-slate-100 text-slate-500 border-slate-300",
};

export default function StaffHome() {
  const navigate = useNavigate();
  const token = localStorage.getItem("staff_token");
  const [employee, setEmployee] = useState(() => {
    try { return JSON.parse(localStorage.getItem("staff_employee") || "null"); } catch { return null; }
  });
  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState(null);
  const [circulars, setCirculars] = useState(null);
  const [notes, setNotes] = useState(null);
  const [perms, setPerms] = useState({});
  const [openId, setOpenId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sig, setSig] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [details, setDetails] = useState({});
  const [debouncedReply, setDebouncedReply] = useState("");
  const [pushState, setPushState] = useState("unknown");
  const [pushBusy, setPushBusy] = useState(false);
  const [pdfData, setPdfData] = useState({});
  const [pdfZoom, setPdfZoom] = useState({});
  const lastActive = useRef(Date.now());
  const fileRef = useRef(null);

  const logout = useCallback((msg) => {
    if (token) fetch(`${API}/staff/logout`, { method: "POST", headers: { "X-Staff-Token": token } }).catch(() => {});
    localStorage.removeItem("staff_token");
    localStorage.removeItem("staff_employee");
    if (msg) toast.error(msg);
    navigate("/staff");
  }, [token, navigate]);

  const staffFetch = useCallback(async (path, options = {}) => {
    const res = await fetch(`${API}${path}`, {
      ...options,
      headers: { "Content-Type": "application/json", "X-Staff-Token": token, ...(options.headers || {}) },
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 401 || res.status === 403) {
      logout(formatApiError(data.detail));
      throw new Error("session-ended");
    }
    if (!res.ok) throw new Error(formatApiError(data.detail));
    return data;
  }, [token, logout]);

  useEffect(() => {
    const bump = () => { lastActive.current = Date.now(); };
    const events = ["pointerdown", "keydown", "touchstart", "scroll"];
    events.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
    const iv = setInterval(() => {
      if (Date.now() - lastActive.current > IDLE_LIMIT) logout("انتهت الجلسة بسبب عدم النشاط");
    }, 15000);
    return () => { events.forEach((ev) => window.removeEventListener(ev, bump)); clearInterval(iv); };
  }, [logout]);

  useEffect(() => {
    if (!token || !employee) { navigate("/staff"); return; }
    staffFetch("/staff/me").then(setEmployee).catch(() => {});
    staffFetch("/staff/inquiries").then(setInquiries).catch(() => setInquiries([]));
    staffFetch("/staff/circulars").then(setCirculars).catch(() => setCirculars([]));
    staffFetch("/staff/notifications").then(setNotes).catch(() => setNotes([]));
    staffFetch("/staff/permissions").then((d) => setPerms(d.permissions || {})).catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedReply(replyText), 350);
    return () => clearTimeout(t);
  }, [replyText]);

  useEffect(() => {
    (async () => {
      if (!pushSupported()) { setPushState("unsupported"); return; }
      await registerServiceWorker();
      if (isIOS() && !isStandalone()) { setPushState("ios-guide"); return; }
      if (Notification.permission === "denied") { setPushState("denied"); return; }
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      setPushState(sub && Notification.permission === "granted" ? "enabled" : "prompt");
    })();
  }, []);

  const enablePushClick = async () => {
    setPushBusy(true);
    try {
      await enablePush(staffFetch);
      setPushState("enabled");
      toast.success("فُعّلت الإشعارات بنجاح");
    } catch (e) {
      if (e.message !== "session-ended") toast.error(e.message || "تعذر تفعيل الإشعارات");
      if (typeof Notification !== "undefined" && Notification.permission === "denied") setPushState("denied");
    } finally { setPushBusy(false); }
  };

  if (!token || !employee) return null;

  const openSection = (id, kind) => {
    if (openId !== null && openId !== id) { setReplyText(""); setSig(null); setAttachment(null); }
    const opening = openId !== id;
    setOpenId(opening ? id : null);
    if (opening && kind === "inq" && !details[id]) {
      staffFetch(`/staff/inquiries/${id}`)
        .then((d) => setDetails((prev) => ({ ...prev, [id]: d })))
        .catch(() => {});
    }
  };

  const pickAttachment = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp", "application/pdf"].includes(f.type)) {
      toast.error("يُقبل فقط ملف صورة أو PDF كمستند للعذر");
      return;
    }
    if (f.size > 10 * 1024 * 1024) { toast.error("الحد الأقصى لحجم المرفق 10MB"); return; }
    setAttachment(f);
  };

  const sendReply = async (inq) => {
    if (!replyText.trim()) { toast.error("اكتب ردك أولاً"); return; }
    if (!sig) { toast.error("وقّع إلكترونياً داخل الإطار قبل الإرسال"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("reply_text", replyText.trim());
      fd.append("signature", sig);
      if (attachment) fd.append("attachment", attachment);
      const res = await fetch(`${API}/staff/inquiries/${inq.id}/reply`,
        { method: "POST", headers: { "X-Staff-Token": token }, body: fd });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401 || res.status === 403) { logout(formatApiError(data.detail)); return; }
      if (!res.ok) throw new Error(formatApiError(data.detail));
      toast.success("تم إرسال ردك بنجاح — أُشعرت الإدارة");
      setOpenId(null);
      setReplyText("");
      setSig(null);
      setAttachment(null);
      setInquiries(await staffFetch("/staff/inquiries"));
      staffFetch(`/staff/inquiries/${inq.id}`)
        .then((d) => setDetails((prev) => ({ ...prev, [inq.id]: d })))
        .catch(() => setDetails((prev) => { const n = { ...prev }; delete n[inq.id]; return n; }));
    } catch (e) { if (e.message !== "session-ended") toast.error(e.message); }
    finally { setSending(false); }
  };

  const loadCircularPdf = async (c) => {
    try {
      const res = await fetch(`${API}/staff/circulars/${c.id}/pdf`, { headers: { "X-Staff-Token": token } });
      if (res.status === 401 || res.status === 403) { logout(); return; }
      if (!res.ok) throw new Error();
      const buf = await res.arrayBuffer();
      const url = URL.createObjectURL(new Blob([buf], { type: "application/pdf" }));
      setPdfData((prev) => ({ ...prev, [c.id]: { buf, url } }));
    } catch { toast.error("تعذر تحميل ملف التعميم"); }
  };

  const signCircular = async (c) => {
    if (!sig) { toast.error("وقّع إلكترونياً داخل الإطار أولاً"); return; }
    setSending(true);
    try {
      await staffFetch(`/staff/circulars/${c.id}/sign`,
        { method: "POST", body: JSON.stringify({ signature: sig }) });
      toast.success("تم توقيع التعميم إقراراً بالاستلام");
      setOpenId(null);
      setCirculars(await staffFetch("/staff/circulars"));
    } catch (e) { if (e.message !== "session-ended") toast.error(e.message); }
    finally { setSending(false); }
  };

  const TABS = [
    ["inquiries", "المساءلات", FileWarning],
    ["circulars", "التعاميم", Megaphone],
    ["evaluation", "تقييمي", ClipboardCheck],
    ["notifications", "التنبيهات", Bell],
    ...(Object.keys(perms).length > 0 ? [["console", "الصلاحيات", KeyRound]] : []),
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="staff-home">
      <header className="bg-emerald-950 text-white sticky top-0 z-20 shadow-lg">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-extrabold text-base truncate" data-testid="staff-name">{employee.name}</h1>
            <p className="text-[11px] text-emerald-300/80 truncate" data-testid="staff-job">{employee.current_job || "موظف"}</p>
          </div>
          <button onClick={() => logout()} data-testid="staff-logout-btn"
            className="flex items-center gap-1.5 text-xs font-semibold text-emerald-200 hover:text-white transition-colors shrink-0">
            <LogOut className="w-4 h-4" /> خروج
          </button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(84px, 1fr))" }} data-testid="staff-tabs">
          {TABS.map(([id, label, Icon]) => (
            <button key={id} onClick={() => (id === "console" ? navigate("/staff/console") : setTab(id))} data-testid={`staff-tab-${id}`}
              className={`rounded-2xl border-2 p-3 text-xs font-bold flex flex-col items-center gap-1.5 transition-colors ${
                tab === id ? "border-emerald-800 bg-emerald-50 text-emerald-950" : "border-slate-200 bg-white text-slate-500"}`}>
              <Icon className="w-5 h-5" /> {label}
            </button>
          ))}
        </div>

        {pushState !== "unsupported" && pushState !== "unknown" && (
          <div className="bg-white rounded-2xl border shadow-sm p-4" data-testid="push-card">
            {pushState === "ios-guide" && (
              <div className="space-y-2" data-testid="ios-install-guide">
                <p className="text-sm font-bold text-emerald-950 flex items-center gap-1.5">
                  <BellRing className="w-4 h-4 text-emerald-800" /> لتصلك الإشعارات على آيفون حتى والتطبيق مغلق:
                </p>
                <ol className="text-xs text-slate-600 leading-6 list-decimal pr-5 space-y-0.5">
                  <li>افتح هذه الصفحة في متصفح <b>سفاري</b>.</li>
                  <li>اضغط زر <b>المشاركة</b> (مربع بداخله سهم للأعلى).</li>
                  <li>اختر <b>«إضافة إلى الشاشة الرئيسية»</b> (Add to Home Screen).</li>
                  <li>افتح «بوابة الموظفين» من الأيقونة الجديدة ثم اضغط «تفعيل الإشعارات».</li>
                </ol>
              </div>
            )}
            {pushState === "prompt" && (
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-slate-600 leading-5 flex items-center gap-1.5">
                  <BellRing className="w-4 h-4 text-emerald-800 shrink-0" />
                  فعّل الإشعارات لتصلك المساءلات والتعاميم فوراً — حتى والتطبيق مغلق
                </p>
                <Button onClick={enablePushClick} disabled={pushBusy} data-testid="push-enable-btn"
                  className="h-9 text-xs bg-emerald-900 hover:bg-emerald-950 text-white shrink-0">
                  {pushBusy ? "جارٍ التفعيل..." : "تفعيل الإشعارات"}
                </Button>
              </div>
            )}
            {pushState === "enabled" && (
              <p className="text-xs font-bold text-emerald-800 flex items-center gap-1.5"
                data-testid="push-status-enabled">
                <BellRing className="w-4 h-4" /> الإشعارات مفعّلة — ستصلك المساءلات والتعاميم فوراً
              </p>
            )}
            {pushState === "denied" && (
              <p className="text-xs font-bold text-amber-800" data-testid="push-status-denied">
                الإشعارات محظورة من إعدادات المتصفح — فعّلها من إعدادات الموقع ثم أعد تحميل الصفحة
              </p>
            )}
          </div>
        )}

        {tab === "inquiries" && (
          <div className="space-y-3" data-testid="staff-inquiries-list">
            {inquiries === null && <p className="text-center text-sm text-slate-400 py-10">جارٍ التحميل...</p>}
            {inquiries?.length === 0 && <p className="text-center text-sm text-slate-400 py-10">لا توجد مساءلات موجهة لك</p>}
            {inquiries?.map((q) => (
              <div key={q.id} className="bg-white rounded-2xl border shadow-sm overflow-hidden" data-testid={`staff-inquiry-${q.id}`}>
                <button onClick={() => openSection(q.id, "inq")} className="w-full text-right p-4 space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-[10px] font-bold rounded-full border px-2 py-0.5 ${STATUS_TONE[q.status]}`}
                      data-testid={`staff-inquiry-status-${q.id}`}>{q.status}</span>
                    <span className="text-[10px] text-slate-400" dir="ltr">{q.date}</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-800 leading-6">{q.summary}</p>
                </button>
                {openId === q.id && (
                  <div className="border-t p-4 space-y-3 bg-slate-50/60">
                    {details[q.id] ? (
                      <LetterView testId={`staff-inquiry-letter-${q.id}`}
                        inquiry={{
                          ...details[q.id].inquiry,
                          reply_text: details[q.id].inquiry.reply_text || debouncedReply,
                          reply_signature_data: details[q.id].inquiry.reply_signature_data || sig || undefined,
                          replied_at: details[q.id].inquiry.replied_at || new Date().toISOString(),
                        }}
                        branding={details[q.id].branding} />
                    ) : (
                      <p className="text-center text-xs text-slate-400 py-6">جارٍ تحميل الاستمارة الرسمية...</p>
                    )}
                    {q.status === "تم الإرسال" ? (
                      <>
                        <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)}
                          rows={4} placeholder="اكتب إفادتك هنا — ستظهر مباشرة داخل الاستمارة أعلاه في جزء الإفادة..."
                          data-testid={`staff-reply-text-${q.id}`}
                          className="w-full rounded-xl border border-slate-200 bg-white p-3 text-sm leading-6 focus:outline-none focus:border-emerald-600" />
                        <div className="space-y-1">
                          <p className="text-[11px] font-bold text-slate-600 flex items-center gap-1">
                            <PenLine className="w-3.5 h-3.5" /> التوقيع الإلكتروني (إقرار بالاطلاع)
                          </p>
                          <SignaturePad onChange={setSig} testId={`staff-reply-sig-${q.id}`} />
                        </div>
                        <div className="space-y-1">
                          <p className="text-[11px] font-bold text-slate-600 flex items-center gap-1">
                            <Paperclip className="w-3.5 h-3.5" /> مستند العذر (اختياري)
                          </p>
                          <input ref={fileRef} type="file" className="hidden"
                            accept="image/png,image/jpeg,image/webp,application/pdf"
                            onChange={pickAttachment} data-testid={`staff-reply-attach-input-${q.id}`} />
                          {attachment ? (
                            <div className="flex items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2"
                              data-testid={`staff-reply-attach-chip-${q.id}`}>
                              <p className="text-xs font-semibold text-emerald-900 truncate">{attachment.name}</p>
                              <button type="button" onClick={() => setAttachment(null)}
                                data-testid={`staff-reply-attach-remove-${q.id}`}
                                className="text-slate-400 hover:text-rose-600 shrink-0"><X className="w-4 h-4" /></button>
                            </div>
                          ) : (
                            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}
                              data-testid={`staff-reply-attach-btn-${q.id}`}
                              className="w-full h-10 text-xs font-bold gap-1.5 border-dashed border-slate-300 text-slate-600 hover:bg-slate-50">
                              <Paperclip className="w-3.5 h-3.5" /> إرفاق مستند العذر (صورة أو PDF — اختياري)
                            </Button>
                          )}
                        </div>
                        <Button onClick={() => sendReply(q)} disabled={sending}
                          data-testid={`staff-reply-submit-${q.id}`}
                          className="w-full h-11 bg-emerald-900 hover:bg-emerald-950 text-white font-bold">
                          {sending ? "جارٍ الإرسال..." : "إرسال الرد"}
                        </Button>
                      </>
                    ) : (
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 space-y-1">
                        <p className="text-[11px] font-bold text-emerald-800 flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" /> ردك المرسل:
                        </p>
                        <p className="text-xs text-slate-700 leading-6">{q.reply_text}</p>
                        {q.has_signature && <p className="text-[10px] text-emerald-700">موقّع إلكترونياً ✓</p>}
                        {q.has_attachment && (
                          <p className="text-[10px] text-emerald-700 flex items-center gap-1"
                            data-testid={`staff-reply-attachment-note-${q.id}`}>
                            <Paperclip className="w-3 h-3" /> أُرفق مستند العذر ✓
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "circulars" && (
          <div className="space-y-3" data-testid="staff-circulars-list">
            {circulars === null && <p className="text-center text-sm text-slate-400 py-10">جارٍ التحميل...</p>}
            {circulars?.length === 0 && <p className="text-center text-sm text-slate-400 py-10">لا توجد تعاميم حالياً</p>}
            {circulars?.map((c) => (
              <div key={c.id} className="bg-white rounded-2xl border shadow-sm overflow-hidden" data-testid={`staff-circular-${c.id}`}>
                <button onClick={() => openSection(c.id, "circ")} className="w-full text-right p-4 space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-[10px] font-bold rounded-full border px-2 py-0.5 ${
                      c.signed ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-amber-100 text-amber-800 border-amber-300"}`}
                      data-testid={`staff-circular-status-${c.id}`}>{c.signed ? "موقّع" : "لم يوقع بعد"}</span>
                    <span className="text-[10px] text-slate-400" dir="ltr">{c.date}</span>
                  </div>
                  <p className="text-sm font-bold text-slate-800">{c.title}</p>
                </button>
                {openId === c.id && (
                  <div className="border-t p-4 space-y-3 bg-slate-50/60">
                    {c.body ? <p className="text-sm text-slate-700 leading-7 whitespace-pre-wrap">{c.body}</p> : null}
                    {c.has_pdf && (
                      pdfData[c.id] ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={() => setPdfZoom((p) => ({ ...p, [c.id]: Math.max(0.5, (p[c.id] || 1) - 0.25) }))}
                              data-testid={`pdf-zoom-out-${c.id}`} aria-label="تصغير"
                              className="w-8 h-8 rounded-lg border bg-white text-slate-600 font-bold hover:bg-slate-50">−</button>
                            <button onClick={() => setPdfZoom((p) => ({ ...p, [c.id]: Math.min(3, (p[c.id] || 1) + 0.25) }))}
                              data-testid={`pdf-zoom-in-${c.id}`} aria-label="تكبير"
                              className="w-8 h-8 rounded-lg border bg-white text-slate-600 font-bold hover:bg-slate-50">+</button>
                            <button onClick={() => window.open(pdfData[c.id].url, "_blank")}
                              data-testid={`pdf-open-tab-${c.id}`}
                              className="h-8 px-3 rounded-lg border bg-white text-[11px] font-bold text-blue-700 hover:bg-blue-50 flex items-center gap-1">
                              <ExternalLink className="w-3.5 h-3.5" /> فتح في تبويب جديد
                            </button>
                          </div>
                          <PdfViewer data={pdfData[c.id].buf} zoom={pdfZoom[c.id] || 1}
                            testId={`staff-circular-pdf-viewer-${c.id}`} />
                        </div>
                      ) : (
                        <Button variant="outline" onClick={() => loadCircularPdf(c)}
                          data-testid={`staff-circular-pdf-${c.id}`}
                          className="w-full h-10 text-xs font-bold gap-1.5 border-blue-300 text-blue-800 hover:bg-blue-50">
                          عرض التعميم (PDF)
                        </Button>
                      )
                    )}
                    {c.signed ? (
                      <p className="text-xs font-bold text-emerald-700 flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" /> تم توقيعك إقراراً بالاستلام
                      </p>
                    ) : (
                      <>
                        <p className="text-[11px] font-bold text-slate-600 flex items-center gap-1">
                          <PenLine className="w-3.5 h-3.5" /> وقّع إقراراً بالاطلاع والاستلام:
                        </p>
                        <SignaturePad onChange={setSig} testId={`staff-circular-sig-${c.id}`} />
                        <Button onClick={() => signCircular(c)} disabled={sending}
                          data-testid={`staff-sign-submit-${c.id}`}
                          className="w-full h-11 bg-emerald-900 hover:bg-emerald-950 text-white font-bold">
                          {sending ? "جارٍ الإرسال..." : "توقيع الاستلام"}
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "notifications" && (
          <div className="space-y-2" data-testid="staff-notifications-list">
            {notes === null && <p className="text-center text-sm text-slate-400 py-10">جارٍ التحميل...</p>}
            {notes?.length === 0 && <p className="text-center text-sm text-slate-400 py-10">لا توجد تنبيهات</p>}
            {notes?.map((n, i) => (
              <div key={i} className="bg-white rounded-2xl border px-4 py-3 flex items-start gap-2.5"
                data-testid={`staff-note-${i}`}>
                <Bell className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-slate-700 leading-6">{n.text}</p>
                  <p className="text-[10px] text-slate-400" dir="ltr">{(n.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "evaluation" && (
          <StaffEvaluation staffFetch={staffFetch} employee={employee} token={token} />
        )}
      </div>
    </div>
  );
}
